import { useRive } from "@rive-app/react-canvas";
import { useT } from "@transifex/react";
import { useEffect, useRef, useState, type RefObject } from "react";
import sidneyThinking from "../../../assets/img/sidney-thinking.svg";
import useBreakpoints, { Breakpoint } from "../../../hooks/useBreakpoints";
import { AreaButton, ButtonColorVariant, ButtonVariant } from "../../teacherArea/AreaButton";
import WelcomeCode from "../../classroomLogin/WelcomeCode";
import OutroCard from "./OutroCard";
import OutroRiveAnimation from "./OutroRiveAnimation";
import { RIVE_COVER_CENTER_LAYOUT } from "../../../constants/RiveConstants";

const ANIMATION_DURATION_MS = 5000;
const PHASE6_URL = "https://www.phase-6.de/?utm_source=phase6-vtg2";

interface OnlineTestOutroProps {
    isTimeExpired: boolean;
    skipCelebration?: boolean;
    /** Only logged-in class members have one; without it the code reminder card is hidden. */
    loginCode?: string;
    /** The pupil asked at signup to see their code again: it opens before the celebration. */
    showCodeFirst?: boolean;
    onCodeNoted?: () => void;
    /** The page's scroll area, which also holds the back link above this view. */
    scrollContainerRef?: RefObject<HTMLDivElement>;
}

const OnlineTestOutro = (props: OnlineTestOutroProps) => {
    const t = useT();
    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    const [animationFinished, setAnimationFinished] = useState(false);
    const [isShowingCode, setIsShowingCode] = useState(!!props.showCodeFirst && !!props.loginCode);

    const codeCardRef = useRef<HTMLDivElement>(null);
    const hasShownCodeRef = useRef(false);

    const t_heading = t("Great, you're done!", { _tags: "OnlineTest,Outro" });
    const t_timeExpired = t("Time is up.", { _tags: "OnlineTest,Outro" });
    const t_answersSent = t("Your answers have been sent to your teacher.", { _tags: "OnlineTest,Outro" });
    const t_resultInfo = t("You will receive your result from your teacher once the grading is complete.", {
        _tags: "OnlineTest,Outro",
    });
    const t_phase6Title = t("With phase6, you're always optimally prepared.", {
        _tags: "OnlineTest,Outro",
    });
    const t_phase6Subtitle = t("Learn the vocabulary from your textbook:\neasily, quickly & successfully!", {
        _tags: "OnlineTest,Outro",
    });
    const t_cta = t("Learn more", { _tags: "OnlineTest,Outro" });
    const t_codeTitle = t("What was my code again?", { _tags: "OnlineTest,Outro" });
    const t_codeInfo = t("You can now log in to the classroom with your code at any time.", {
        _tags: "OnlineTest,Outro",
    });
    const t_showCode = t("Show my code", { _tags: "OnlineTest,Outro" });

    const { RiveComponent: WavingRiveComponent } = useRive({
        src: require("../../../assets/rive/sidney_waving.riv"),
        autoplay: true,
        layout: RIVE_COVER_CENTER_LAYOUT,
    });

    const { RiveComponent: ThumbsUpRiveComponent } = useRive({
        src: require("../../../assets/rive/sidney_thumbs_up.riv"),
        autoplay: true,
        layout: RIVE_COVER_CENTER_LAYOUT,
    });

    // Held back while the code is open, so a code shown first doesn't eat the celebration.
    useEffect(() => {
        if (isShowingCode || animationFinished) return;
        const timer = setTimeout(() => setAnimationFinished(true), ANIMATION_DURATION_MS);
        return () => clearTimeout(timer);
    }, [isShowingCode, animationFinished]);

    const handleCodeNoted = () => {
        setIsShowingCode(false);
        props.onCodeNoted?.();
    };

    // The code card sits below the fold on mobile, so without this both swaps would land mid-page.
    useEffect(() => {
        if (isShowingCode) {
            hasShownCodeRef.current = true;
            props.scrollContainerRef?.current?.scrollTo({ top: 0 });
        } else if (hasShownCodeRef.current) {
            codeCardRef.current?.scrollIntoView({ block: "center" });
        }
    }, [isShowingCode, props.scrollContainerRef]);

    const renderAnimationPhase = () => (
        <div
            className="flex flex-col items-center justify-center flex-1 w-full"
            data-testid="online-test-outro"
        >
            <p className="text-xxl font-semibold text-p6-grey-dark text-center">{t_heading}</p>
            <div className={getStyleBasedOnBreakpoint(Breakpoint.MD, "w-[428px] h-[428px]", "w-[280px] h-[280px]")}>
                <WavingRiveComponent />
            </div>
        </div>
    );

    const renderTimeExpiredPhase = () => (
        <div
            className="flex flex-col items-center justify-center flex-1 w-full"
            data-testid="online-test-outro"
        >
            <div className={getStyleBasedOnBreakpoint(Breakpoint.MD, "w-[428px] h-[428px]", "w-[280px] h-[280px]")}>
                <ThumbsUpRiveComponent />
            </div>
            <p className="text-xl font-semibold text-p6-grey-dark text-center">{t_timeExpired}</p>
            <p className="text-l text-p6-grey-dark text-center">{t_answersSent}</p>
        </div>
    );

    const renderCodeCard = () => {
        if (!props.loginCode) return null;

        return (
            <div
                ref={codeCardRef}
                className="w-full"
            >
                <OutroCard
                    illustration={
                        <div
                            className={`flex h-[150px] items-center ${getStyleBasedOnBreakpoint(
                                Breakpoint.MD,
                                "my-l ml-m",
                                "mb-m"
                            )}`}
                        >
                            <img
                                src={sidneyThinking}
                                alt=""
                                aria-hidden
                                className="size-[130px]"
                            />
                        </div>
                    }
                    title={t_codeTitle}
                    bodyTexts={[t_codeInfo]}
                    sidneyPosition="right"
                >
                    <div className={getStyleBasedOnBreakpoint(Breakpoint.MD, "self-start", "w-full")}>
                        <AreaButton
                            variant={ButtonVariant.SECONDARY}
                            colorVariant={ButtonColorVariant.DEFAULT_WHITE}
                            role="button"
                            onClick={() => setIsShowingCode(true)}
                            data-testid="online-test-outro-show-code"
                            className="w-full"
                        >
                            {t_showCode}
                        </AreaButton>
                    </div>
                </OutroCard>
            </div>
        );
    };

    const renderCodeView = () => (
        <div
            className="flex flex-1 flex-col items-center pt-xxxxl pb-xxl w-full"
            data-testid="online-test-outro-code"
        >
            <WelcomeCode
                loginCode={props.loginCode ?? ""}
                onDone={handleCodeNoted}
            />
        </div>
    );

    const renderInfoPhase = () => (
        <div
            className="flex flex-col items-center justify-center gap-s w-full flex-1 pt-s"
            data-testid="online-test-outro"
        >
            <OutroCard
                illustration={<OutroRiveAnimation src={require("../../../assets/rive/sidney_thumbs_up.riv")} />}
                title={t_heading}
                bodyTexts={[t_answersSent, t_resultInfo]}
                sidneyPosition="right"
            />
            <OutroCard
                illustration={
                    <OutroRiveAnimation
                        src={require("../../../assets/rive/sidney_reading.riv")}
                        stateMachines="sid_read"
                    />
                }
                title={t_phase6Title}
                bodyTexts={[t_phase6Subtitle]}
            >
                <div className={getStyleBasedOnBreakpoint(Breakpoint.MD, "w-[348px] ml-auto", "w-full")}>
                    <AreaButton
                        variant={ButtonVariant.PRIMARY}
                        role="link"
                        href={PHASE6_URL}
                        openInNewTab
                    >
                        {t_cta}
                    </AreaButton>
                </div>
            </OutroCard>
            {renderCodeCard()}
        </div>
    );

    if (isShowingCode) return renderCodeView();

    if (!animationFinished && !props.skipCelebration) {
        return props.isTimeExpired ? renderTimeExpiredPhase() : renderAnimationPhase();
    }

    return renderInfoPhase();
};

export default OnlineTestOutro;
